import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Loader2, Facebook, Twitter, Mail } from "lucide-react";
import MainLayout from "@/layouts/MainLayout";
import { Helmet } from "react-helmet-async";
import { Navigate } from "react-router-dom";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import DOMPurify from "dompurify";

interface NewsArticle {
  id: string;
  title: string;
  slug: string;
  author: string | null;
  featured_image_url: string | null;
  category: string;
  content: string | null;
  published_at: string | null;
  created_at: string;
}

const NewsArticlePage = () => {
  const { settings, isLoading: settingsLoading } = useSiteSettings();
  const { slug } = useParams<{ slug: string }>();

  const { data: article, isLoading } = useQuery({
    queryKey: ["news-article", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("news_articles")
        .select("*")
        .eq("slug", slug)
        .eq("is_published", true)
        .single();

      if (error) throw error;
      return data as NewsArticle;
    },
    enabled: !!slug,
  });

  const { data: nextArticle } = useQuery({
    queryKey: ["next-article", article?.published_at],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("news_articles")
        .select("id, title, slug, featured_image_url, content, published_at, created_at")
        .eq("is_published", true)
        .lt("published_at", article?.published_at || article?.created_at)
        .order("published_at", { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== "PGRST116") throw error;
      return data as NewsArticle | null;
    },
    enabled: !!article,
  });

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  if (isLoading) {
    return (
      <MainLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </MainLayout>
    );
  }

  if (!article) {
    return (
      <MainLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Article Not Found</h1>
            <Link to="/news" className="btn-outline-dark">
              Back to News
            </Link>
          </div>
        </div>
      </MainLayout>
    );
  }

  const articleDate = article.published_at || article.created_at;
  const formattedDate = format(new Date(articleDate), "MMMM d, yyyy");

  // Extract excerpt from content for next article
  const getExcerpt = (content: string | null, maxLength: number = 150) => {
    if (!content) return "";
    const stripped = content.replace(/<[^>]*>/g, "");
    return stripped.length > maxLength ? stripped.substring(0, maxLength) + "..." : stripped;
  };

  if (!settingsLoading && settings.news_enabled === "false") {
    return <Navigate to="/" replace />;
  }

  return (
    <MainLayout>
      <Helmet>
        <title>{article.title} | Rogue Skull Games</title>
        <meta name="description" content={getExcerpt(article.content, 160)} />
        <link rel="canonical" href={`https://rogueskullgames.com/news/${slug}`} />
        <meta property="og:title" content={`${article.title} | Rogue Skull Games`} />
        <meta property="og:description" content={getExcerpt(article.content, 160)} />
        <meta property="og:url" content={`https://rogueskullgames.com/news/${slug}`} />
        <meta property="og:type" content="article" />
        {article.featured_image_url && (
          <meta property="og:image" content={article.featured_image_url} />
        )}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            datePublished: articleDate,
            dateModified: articleDate,
            author: {
              "@type": article.author ? "Person" : "Organization",
              name: article.author || "Rogue Skull Games",
            },
            publisher: {
              "@type": "Organization",
              name: "Rogue Skull Games",
              logo: {
                "@type": "ImageObject",
                url: "https://rogueskullgames.com/icon-512.png",
              },
            },
            url: `https://rogueskullgames.com/news/${slug}`,
            ...(article.featured_image_url ? { image: article.featured_image_url } : {}),
          })}
        </script>
      </Helmet>

      <article className="bg-white overflow-x-hidden">
        {/* Article Header */}
        <div className="container mx-auto px-4 md:px-6 pt-[140px] md:pt-[180px] pb-8 max-w-4xl">
          <p className="text-blood-red uppercase tracking-widest font-semibold mb-4 text-sm md:text-base">
            {formattedDate}
          </p>
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[hsl(0,0%,10%)] uppercase tracking-wide leading-tight mb-4">
            {article.title}
          </h1>
          {article.author && <p className="text-[hsl(0,0%,40%)] italic">by {article.author}</p>}
        </div>

        {/* Featured Image */}
        {article.featured_image_url && (
          <div className="container mx-auto px-4 md:px-6 max-w-5xl mb-8 md:mb-12">
            <img
              src={article.featured_image_url}
              alt={article.title}
              className="w-full h-auto object-cover rounded-lg"
            />
          </div>
        )}

        {/* Article Content */}
        <div className="container mx-auto px-4 md:px-6 max-w-4xl pb-12">
          {article.content && (
            <div
              className="prose prose-lg max-w-none text-[hsl(0,0%,20%)]
                prose-headings:font-serif prose-headings:text-[hsl(0,0%,10%)]
                prose-p:text-[hsl(0,0%,20%)] prose-p:leading-relaxed prose-p:mb-6
                prose-img:rounded-lg
                prose-a:text-blood-red prose-a:no-underline hover:prose-a:underline
                prose-img:my-8
                prose-strong:text-[hsl(0,0%,10%)]
                [&>p:empty]:h-4"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content) }}
            />
          )}

          {/* Share Section */}
          <div className="mt-12 pt-8 border-t border-[hsl(0,0%,85%)]">
            <div className="flex items-center gap-4">
              <span className="text-sm text-[hsl(0,0%,40%)]">Share this story:</span>
              <div className="flex items-center gap-6">
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[hsl(0,0%,20%)] hover:text-blood-red transition-colors"
                  aria-label="Share on Facebook"
                >
                  <Facebook className="w-[18px] h-[18px]" />
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(article.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[hsl(0,0%,20%)] hover:text-blood-red transition-colors"
                  aria-label="Share on Twitter"
                >
                  <Twitter className="w-[18px] h-[18px]" />
                </a>
                <a
                  href={`mailto:?subject=${encodeURIComponent(article.title)}&body=${encodeURIComponent(shareUrl)}`}
                  className="text-[hsl(0,0%,20%)] hover:text-blood-red transition-colors"
                  aria-label="Share via Email"
                >
                  <Mail className="w-[18px] h-[18px]" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Next Article Section */}
        {nextArticle && (
          <section className="border-t border-[hsl(0,0%,85%)] py-12 md:py-16 bg-white">
            <div className="container mx-auto px-4 md:px-6 max-w-5xl">
              <h2 className="text-2xl md:text-3xl font-bold text-[hsl(0,0%,10%)] text-center mb-12 uppercase tracking-wide">
                Next
              </h2>
              <Link to={`/news/${nextArticle.slug}`} className="grid md:grid-cols-2 gap-8 items-center group">
                {nextArticle.featured_image_url && (
                  <div className="overflow-hidden rounded-lg">
                    <img
                      src={nextArticle.featured_image_url}
                      alt={nextArticle.title}
                      className="w-full aspect-[4/3] object-cover transition-transform duration-500 group-hover:scale-105 rounded-lg"
                    />
                  </div>
                )}
                <div>
                  <p className="text-xs text-[hsl(0,0%,40%)] uppercase tracking-widest mb-3">
                    {format(new Date(nextArticle.published_at || nextArticle.created_at), "yyyy MMM dd")}
                  </p>
                  <h3 className="text-xl md:text-2xl font-bold text-[hsl(0,0%,10%)] uppercase tracking-wide mb-4 group-hover:text-blood-red transition-colors">
                    {nextArticle.title}
                  </h3>
                  <p className="text-[hsl(0,0%,40%)] text-sm leading-relaxed mb-4">
                    {getExcerpt(nextArticle.content, 200)}
                  </p>
                  <span className="text-xs font-semibold uppercase tracking-widest text-[hsl(0,0%,10%)] group-hover:text-blood-red transition-colors">
                    Continue to {nextArticle.title}
                  </span>
                </div>
              </Link>
            </div>
          </section>
        )}

        {/* View All News Button */}
        <div className="text-center pb-16 bg-white">
          <Link
            to="/news"
            className="inline-flex items-center justify-center gap-2 border border-[hsl(0,0%,20%)] text-[hsl(0,0%,10%)] bg-transparent hover:border-blood-red hover:bg-blood-red hover:text-white transition-all duration-300 px-7 py-3.5 uppercase tracking-[0.2em] text-xs font-semibold"
          >
            View All News
          </Link>
        </div>
      </article>
    </MainLayout>
  );
};

export default NewsArticlePage;
